import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-vnavy flex items-center justify-center px-6 grain relative">
      <button
        data-testid="back-to-site"
        onClick={() => navigate("/")}
        className="absolute top-6 left-6 z-10 flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al sitio
      </button>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <span className="inline-flex items-center bg-white rounded-xl px-6 py-4 shadow-2xl shadow-black/30 mb-6">
            <img src="/logo-vertice-h.webp" alt="Grupo Vértice CR" className="h-14 w-auto object-contain" />
          </span>
          <h1 className="font-serif text-3xl text-white">Panel Administrativo</h1>
          <p className="text-gray-500 text-sm mt-1">Acceso exclusivo del super administrador</p>
        </div>

        <form onSubmit={submit} data-testid="login-form" className="bg-vnavy-800 border border-white/10 rounded-2xl p-8 space-y-5">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Correo electrónico</label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="grupoverticecr@gmail.com"
              className="bg-vnavy border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Contraseña</label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-vnavy border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-vteal-light px-8 py-3.5 text-vnavy font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            <Lock className="h-4 w-4" />
            {loading ? "Ingresando..." : "Iniciar sesión"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
